import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence, GoogleAuthProvider, TwitterAuthProvider, GithubAuthProvider, signInWithPopup, signInWithEmailAndPassword } from "firebase/auth";

import {getFirebaseConfig, defaultState} from '@/js/store';
import {whoAmI, mergeControls, updateAuth, showToaster} from '@/js/apollo';

// serialize user to store
// this is mainly to make it reactive so that pictures etc can be updated
// wont write token info to store as i'd rather get that using currentUser

const storeFirebaseUser = user => {
  // the user.getidtoken is asynchronous

  // possible TODO firebase allows metadata to be retrieved also
  // TODO this is not the right way to do this, as I dont want all this stuff
  // persisted in apollo cache
  // rework this to indicate a change of user in cache
  // but get the details from currentUser
  if (!user) return updateAuth(defaultState.ProviderAuth);

  return user.getIdToken().then(token =>
    updateAuth({
      uid: user.uid,
      displayName: user.displayName,
      providerID: user.providerId,
      emailVerified: user.emailVerified,
      token,
      photoUrl: user.photoURL,
      email: user.email,
      isAnonymous: user.isAnonymous,
      userID: null,
      ready: false,
    })
  );
};

// set up charactertics of a twitter sign in
const setProviderTwitter = () => {
  const provider = new TwitterAuthProvider();
  provider.setCustomParameters({
    allow_signup: 'true',
  });
  return provider;
};

// set up charactertics of a google sign in
const setProviderGoogle = () => {
  const provider = new GoogleAuthProvider();
  provider.addScope('profile');
  provider.addScope('email');
  provider.setCustomParameters({
    allow_signup: 'true',
  });
  return provider;
};



// set up characteristics of github sign in
const setProviderGithub = () => {
  const provider = new GithubAuthProvider();
  provider.addScope('user:email');
  provider.addScope('read:user');
  provider.setCustomParameters({
    allow_signup: 'true',
  });
  return provider;
};


let providers = null;
let auth = null;
let app = null;

// do the login flow common between providers
const executeLogin = ({service}) => {
  // drop any .xxx suffixes in type coming back from link accounts
  const type = service.replace(/\..*/, '');
  const provider = providers[type];
  if (!provider) {
    showToaster({
      header: 'sign in error',
      type: 'bug',
      message: `unknown signing type ${type}`,
      error: null,
    });
    return Promise.reject(type);
  } else {
    return signInWithPopup(auth, provider)
      .catch(error => {
        if (error.code === 'auth/account-exists-with-different-credential') {
          return linkAccounts({error, service});
        } else {
          showToaster({
            header: 'sign in error',
            type: 'warning',
            message: error.code,
            error,
          });
        }
      })
      .then(res => {
        return res;
      });
  }
};

const linkAccounts = ({error, service}) => {
  // so this is complicated - trying to login with a social provider
  const {credential, email} = error;

  // save this for later
  fidauth.credential = credential;

  // get the favorite type of login
  return auth.fetchSignInMethodsForEmail(email).then(methods => {
    // so it was a different type of login as favourite
    // if its a password as the favorite, then we need an additional dialog
    // to get the filmID password
    // enabled below
    return mergeControls({
      service: methods[0],
      newService: service,
      email,
      showLinkDialog: true,
      showPasswordDialog: methods[0] === 'password',
    });
  });
};

const fidauth = {
  activeUser: null,
  whoAmI: () => whoAmI(),
  getIdToken: () => {
    // because get id token is async
    const {activeUser} = fidauth;
    return activeUser ? activeUser.getIdToken() : Promise.resolve(null);
  },
  credential: null,
  install: (Vue) => {
    // initialize firebase on install
    app = initializeApp(getFirebaseConfig());
    auth = getAuth(app)
    auth.useDeviceLanguage();
    setPersistence(auth, browserLocalPersistence);
    // set up the social providers being enabled
    providers = {
      google: setProviderGoogle(),
      github: setProviderGithub(),
      twitter: setProviderTwitter(),
    };
    // deal with a change in user signed in status
    auth.onAuthStateChanged(user => {
      if (!fidauth.activeUser || !user || fidauth.activeUser.uid !== user.uid) {
        fidauth.activeUser = null;

        return storeFirebaseUser(user).then(auth => {
          const aob = auth.data.updateProviderAuth;
          fidauth.activeUser = user;
          return fidauth.whoAmI().then(r => {
            // if not found then this will be a null result .. when someone just logged out for example
            const {data} = r;
            const wob = data && data.WhoAmI;
            aob.userID = wob && wob.userID;
            aob.ready = true;
            return updateAuth(aob);
          });
        });
        // TODO - something about clearing localstorage on change of user
      }
    });

    // Vue plugin to call auth - will be accessible in componnents as this.$auth
    Vue.prototype.$auth = {
      forgotPassword: ({email}) => {
        // comment this out for testing to avoid actually resetting
        return auth
          .sendPasswordResetEmail(email)
          .then(() => {
            mergeControls({email, showForgotDialog: true});
          })
          .catch(error => {
            showToaster({
              header: 'reminder error',
              type: 'error',
              message: `${error.message} sending reminder to ${email}`,
              error,
            });
          });
      },
      userLinkComplete: ({service, email, password}) => {
        // if the service is a password,, we should have picked up a password by the time we get here
        // TODO this hasnt been tested yet
        if (service === 'password') {
          if (!password || !email) {
            showToaster({
              header: 'account linking error',
              type: 'bug',
              message: `failed linking to password account, no password or email available ${email}`,
              error: null,
            });
          } else {
            return signInWithEmailAndPassword(auth, email, password)
              .then(user => {
                // link the credentials from the service with user login/password
                return user.link(fidauth.credential);
              });
          }
        } else {
          return executeLogin({service}).then(({user}) => {
            // link the two providers.. we'd have stored the credential previously
            // dont want to put that in the store
            return user.linkAndRetrieveDataWithCredential(fidauth.credential);
          });
        }
      },
      create: ({service, email, password}) => {
        if (service !== 'password') {
          showToaster({
            header: 'user create error',
            type: 'bug',
            message: `service type ${service} not supported`,
            error: null,
          });
        } else {
          auth.createUserWithEmailAndPassword(email, password)
            .then(result => {
              const {user} = result;
              // need to verify email
              return user.sendEmailVerification().then(() => {
                return mergeControls({
                  service,
                  email,
                  showVerificationDialog: true,
                });
              });
            })
            .catch(error => {
              showToaster({
                header: 'user create error',
                type: 'error',
                message: error.message,
                error: error,
              });
            });
        }
      },
      login: ({service, email, password}) => {
        // handle the login
        if (service === 'password') {
          return signInWithEmailAndPassword(auth, email, password)
            .catch(error => {
              // Handle Errors here.
              showToaster({
                header: 'password sign in error',
                type: 'error',
                message: error.message,
                error,
              });
            });
        } else {
          return executeLogin({service});
        }
      },
      logout: () => {
        return auth.signOut()
          .then(() => {
            // Sign-out successful
            showToaster({
              header: 'sign out',
              type: 'info',
              message: 'completed',
              error: null,
            });
          })
          .catch(error => {
            showToaster({
              header: 'sign out',
              type: 'error',
              message: error.message,
              error,
            });
          });
      },
    };
  },
};

export default fidauth;
